import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Box } from '@mui/material';
import ClientDetailMain from "../../components/ClientDetailMain/ClientDetailMain";
import Csmnotes from '../../components/CSMNotes/csmnotes';
import CallReviews from '../../components/CallReviews/CallReviews';
import ClientEngagementTrendsClients from '../../components/ClientEngagementTrendsClients/ClientEngagementTrendsClients';
import OpenTask from '../../components/OpenTask/OpenTask';
import InteractionHistory from '../../components/InteractionHistory/InteractionHistory';
import CSMSelector from '../../components/CSMSelector/CSMSelector';
import ContactSyncStatus from '../../components/ContactSyncStatus/ContactSyncStatus';
import './Client.css';
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Client = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const workspaceId = Auth.getWorkspaceId();
    const [clientDetails, setClientDetails] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [confirmationText, setConfirmationText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [isArchived, setIsArchived] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [csmChanged, setCSMChanged] = useState(false);
    
    const user = Auth.getUser();
    const isAdmin = user?.admin || false; 

    useEffect(() => {
        const fetchClientDetails = async () => {
            try {
                if (!clientId || !workspaceId) {
                    throw new Error('Client ID or Workspace ID is missing');
                }
                
                const response = await axios.get(`${API_URL}/clients/get-client-by-id`, {
                    params: {
                        clientId: clientId,
                        workspaceId: workspaceId
                    }
                });
                
                if (!response.data) {
                    throw new Error('Client not found for this workspace');
                }
                
                setClientDetails(response.data); 
                setIsArchived(response.data.archived || false); 
            } catch (error) {
                console.error('Error fetching client details:', error);
                alert(error.response?.data?.message || error.message);
                navigate('/clients');
            }
        };

        fetchClientDetails();
    }, [clientId, workspaceId, navigate]);

    const toggleArchiveStatus = async () => {
        if (!workspaceId || !clientDetails) {
            alert('Workspace ID or client details are missing. Please try again.');
            return;
        }

        setIsUpdating(true);
        try {
            const newArchivedStatus = !isArchived; 
            const updatedClient = {
                id: clientId,
                workspaceId: workspaceId,
                companyId: clientDetails.companyId || "",
                firstName: clientDetails.firstName || "",
                lastName: clientDetails.lastName || "",
                clientRetentionScore: clientDetails.clientRetentionScore || 0,
                icp: clientDetails.icp || "",
                touchpointFrequency: clientDetails.touchpointFrequency || "",
                onboardingDate: clientDetails.onboardingDate || new Date().toISOString(),
                phoneNumber: clientDetails.phoneNumber || "",
                email: clientDetails.email || "",
                address: clientDetails.address || "",
                office: clientDetails.office || "",
                netPromoterScore: clientDetails.netPromoterScore || 0,
                clientEffortScores: clientDetails.clientEffortScores || 0,
                billingStatus: clientDetails.billingStatus || "",
                lifeTimeValue: clientDetails.lifeTimeValue || 0,
                archived: String(newArchivedStatus), 
            };

            await axios.put(`${API_URL}/clients/clients?workspaceId=${workspaceId}`, updatedClient, {
                headers: { "Content-Type": "application/json" },
            });

            setIsArchived(newArchivedStatus); 

            setTimeout(() => {
                navigate('/clients');
            }, 1000);
        } catch (error) {
            console.error('Error updating archive status:', error.response?.data);
            alert('Failed to update archive status');
        } finally {
            setIsUpdating(false); 
        }
    };

    const handleCSMChange = () => {
        setCSMChanged(true);
    };

    const handleDeleteClick = async () => {
        setIsDeleting(true);
        try {
            if (!clientId || !workspaceId) {
                throw new Error("Client ID or Workspace ID is missing. Cannot proceed with deletion.");
            }
    
            const response = await axios({
                method: 'delete',
                url: `${API_URL}/clients/clients`,
                data: { 
                    id: clientId,
                    workspaceId: workspaceId 
                },
                headers: { "Content-Type": "application/json" }
            });

            navigate('/clients');
        } catch (error) {
            console.error('Full error object:', error);
            console.error('Error response data:', error.response?.data);
            
            const errorMessage = error.response?.data?.message || 
                                error.message || 
                                'Failed to delete client';
            alert(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleConfirmDelete = () => {
        if (confirmationText === 'delete') {
            handleDeleteClick();
            setIsDialogOpen(false);
        } else {
            alert('Please type "delete" to confirm.');
        }
    };

    return (
        <div className="client-container">
            <div className='column_left'>
                <ClientDetailMain clientId={clientId} />
            </div>
            <div className='column_center'>
                <Csmnotes />
                <ClientEngagementTrendsClients />
            </div>
            <div className={`column_right ${isAdmin ? 'has-csm-selector' : ''}`}>
                <CSMSelector clientId={clientId} onCSMChange={handleCSMChange} />
                <ContactSyncStatus clientId={clientId} />
                <CallReviews clientId={clientId} reducedHeight={isAdmin} />
                {/* OpenTask component hidden*/}
                {/* <OpenTask reducedHeight={isAdmin} /> */}
                <InteractionHistory clientId={clientId} reducedHeight={isAdmin} />
            </div>

            <div className="button-archive-container">
                {isArchived ? (
                    <Button
                        sx={{
                            backgroundColor: '#FFD700',
                            color: '#572AF1',
                            border: 'none',
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '12px 24px',
                            minWidth: '170px',
                            height: '50px',
                            position: 'relative',
                            '&:hover': {
                                backgroundColor: '#E0D8FA', 
                            },
                        }}
                        onClick={toggleArchiveStatus}
                        disabled={isUpdating} 
                    >
                        {isUpdating ? 'Reactivating...' : 'Reactivate'}
                        {/* Info Icon with Tooltip positioned inside button */}
                        <Tooltip
                            title={
                                <span style={{ fontSize: "1.15rem", color: "#fff", lineHeight: 1.6 }}>
                                    <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#FFD700" }}>Reactivating</span><br /><br />
                                    <span style={{ fontWeight: 600, color: "#FFD700" }}>What does Reactivating do?</span><br />
                                    Restores an archived client back to active status in the system.<br /><br />
                                    The client will reappear in all active views and can be managed normally.<br /><br />
                                    All historical data and records are preserved and restored.<br /><br />
                                    <span style={{ fontWeight: 600, color: "#FFD700" }}>📊 Impact on Churn:</span><br />
                                    Reactivated clients are no longer counted in churn metrics — they are treated as active clients again.
                                </span>
                            }
                            arrow
                            placement="right"
                            slotProps={{
                                popper: {
                                    sx: {
                                        '& .MuiTooltip-tooltip': {
                                            backgroundColor: '#181828',
                                            color: '#fff',
                                            fontSize: '1.15rem',
                                            padding: '18px 22px',
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 16px rgba(0,0,0,0.32)'
                                        }
                                    }
                                }
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    fontSize: 16,
                                    color: "#572AF1",
                                    background: "#fff",
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                                    cursor: "pointer",
                                    zIndex: 2,
                                    width: "20px",
                                    height: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            />
                        </Tooltip>
                    </Button>
                ) : (
                    <Button
                        sx={{
                            backgroundColor: '#EEEAFC',
                            color: '#572AF1',
                            border: 'none',
                            boxShadow: 'none',
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '12px 24px',
                            minWidth: '170px',
                            height: '50px',
                            position: 'relative',
                            '&:hover': {
                                backgroundColor: '#E0D8FA',
                            },
                        }}
                        onClick={toggleArchiveStatus}
                        disabled={isUpdating} 
                    >
                        {isUpdating ? 'Archiving...' : 'Archive'}
                        {/* Info Icon with Tooltip positioned inside button */}
                        <Tooltip
                            title={
                                <span style={{ fontSize: "1.15rem", color: "#fff", lineHeight: 1.6 }}>
                                    <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#FFD700" }}>Archiving</span><br /><br />
                                    <span style={{ fontWeight: 600, color: "#FFD700" }}>What does Archiving do?</span><br />
                                    Marks a client as inactive, but keeps all historical data in the system.<br /><br />
                                    The client will no longer appear in active views, but can be restored or reviewed later.<br /><br />
                                    Used when a client has left but you want to retain their record.<br /><br />
                                    <span style={{ fontWeight: 600, color: "#FFD700" }}>📊 Impact on Churn:</span><br />
                                    Archived clients are included in churn metrics — they are counted as lost clients for reporting purposes.
                                </span>
                            }
                            arrow
                            placement="right"
                            slotProps={{
                                popper: {
                                    sx: {
                                        '& .MuiTooltip-tooltip': {
                                            backgroundColor: '#181828',
                                            color: '#fff',
                                            fontSize: '1.15rem',
                                            padding: '18px 22px',
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 16px rgba(0,0,0,0.32)'
                                        }
                                    }
                                }
                            }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px",
                                    fontSize: 16,
                                    color: "#572AF1",
                                    background: "#fff",
                                    borderRadius: "50%",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                                    cursor: "pointer",
                                    zIndex: 2,
                                    width: "20px",
                                    height: "20px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            />
                        </Tooltip>
                    </Button>
                )}
                <Button
                    sx={{
                        backgroundColor: 'rgba(245,43,83,0.72)',
                        color: 'white',
                        border: 'none',
                        boxShadow: 'none',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '12px 24px',
                        minWidth: '170px',
                        height: '50px',
                        position: 'relative',
                        '&:hover': {
                            backgroundColor: 'rgba(245,43,83,0.63)',
                        },
                    }}
                    onClick={() => setIsDialogOpen(true)}
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Deleting client...' : 'Delete Client'}
                    {/* Info Icon with Tooltip positioned inside button */}
                    <Tooltip
                        title={
                            <span style={{ fontSize: "1.15rem", color: "#fff", lineHeight: 1.6 }}>
                                <span style={{ fontWeight: 700, fontSize: "1.2rem", color: "#FFD700" }}>Deleting</span><br /><br />
                                <span style={{ fontWeight: 600, color: "#FFD700" }}>What does Deleting do?</span><br />
                                Permanently removes the client and all related data from the system.<br /><br />
                                This action cannot be undone, and the record is no longer recoverable.<br /><br />
                                Used when a client record was created by mistake, is a duplicate, or is no longer relevant.<br /><br />
                                <span style={{ fontWeight: 600, color: "#FFD700" }}>📊 Impact on Churn:</span><br />
                                Deleted clients are not counted in churn metrics — it's as if they never existed in your data.
                            </span>
                        }
                        arrow
                        placement="right"
                        slotProps={{
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#181828',
                                        color: '#fff',
                                        fontSize: '1.15rem',
                                        padding: '18px 22px',
                                        borderRadius: '12px',
                                        boxShadow: '0 2px 16px rgba(0,0,0,0.32)'
                                    }
                                }
                            }
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                fontSize: 16,
                                color: "rgba(245,43,83,0.72)",
                                background: "#fff",
                                borderRadius: "50%",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
                                cursor: "pointer",
                                zIndex: 2,
                                width: "20px",
                                height: "20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        />
                    </Tooltip>
                </Button>
            </div>

            <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
                <DialogTitle>Confirm Client Deletion</DialogTitle>
                <DialogContent>
                    <p>
                        You are about to permanently delete all information related to this client. This action cannot be undone.
                    </p>
                    <TextField
                        fullWidth
                        label="Type 'delete' to confirm"
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        disabled={confirmationText !== 'delete'}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Client;